/* @jsx h */
import { defineComponent, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { ProcedureSummary } from "../shared/types";
import { scheduleDays, weekdayNames } from "../shared/dates";

type Props = {
  procedure: ProcedureSummary;
  onRun: () => void;
  onEdit: (focusSchedule: boolean) => void;
  onHistory: () => void;
  onDelete: () => void;
};

export const ProcedureCard = defineComponent<Props>({
  name: "ProcedureCard",
  props: ["procedure", "onRun", "onEdit", "onHistory", "onDelete"],
  setup(props) {
    const { t } = useLocalization();
    const names = weekdayNames("short");

    return () => {
      const p = props.procedure;
      const days = scheduleDays(
        p.schedule_days,
        { everyDay: t("workday.schedule.every_day"), weekdays: t("workday.schedule.weekdays"), weekends: t("workday.schedule.weekends") },
        names,
      );
      const last = p.last_run;
      return (
        <div class={["flex flex-col rounded-xl border border-base-300 bg-base-100 p-5 shadow-sm", p.active ? "" : "opacity-60"].join(" ")}>
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <h3 class="text-lg font-bold leading-tight text-base-content">{p.name}</h3>
              <div class="mt-1 text-sm text-base-content/60">{t(`workday.department.${p.department}`)}</div>
            </div>
            <div class="dropdown dropdown-end">
              <div tabindex={0} role="button" class="btn btn-sm btn-outline shrink-0 flex-nowrap whitespace-nowrap border-base-300">
                {t("workday.common.actions")}
                <span class="material-symbols-outlined text-base">expand_more</span>
              </div>
              <ul tabindex={0} class="menu dropdown-content z-20 w-48 rounded-box border border-base-300 bg-base-100 p-1 shadow-lg">
                <li><a onClick={() => props.onRun()}><span class="material-symbols-outlined text-base">play_arrow</span>{t("workday.procedures.run_now")}</a></li>
                <li><a onClick={() => props.onEdit(false)}><span class="material-symbols-outlined text-base">edit</span>{t("workday.common.edit")}</a></li>
                <li><a onClick={() => props.onHistory()}><span class="material-symbols-outlined text-base">history</span>{t("workday.procedures.view_history")}</a></li>
                <li><a class="text-error" onClick={() => props.onDelete()}><span class="material-symbols-outlined text-base">delete</span>{t("workday.common.delete")}</a></li>
              </ul>
            </div>
          </div>

          <div class="mt-4 space-y-2 text-sm">
            {days && (
              <div class="flex items-start gap-2 text-base-content/80">
                <span class="material-symbols-outlined text-base text-base-content/50">calendar_month</span>
                <span>
                  {p.schedule_time
                    ? t("workday.schedule.scheduled_at").replace("{days}", days).replace("{time}", p.schedule_time)
                    : t("workday.schedule.scheduled").replace("{days}", days)}
                </span>
              </div>
            )}
            <div class="flex items-center gap-2 text-base-content/60">
              <span class="material-symbols-outlined text-base text-base-content/40">checklist</span>
              {t("workday.procedures.step_count").replace("{count}", String(p.step_count))}
            </div>
            {last && (
              <div class="flex items-center gap-2 text-base-content/60">
                <span class={["material-symbols-outlined text-base", last.status === "completed" ? (last.issues > 0 ? "text-warning" : "text-success") : "text-base-content/40"].join(" ")}>
                  {last.status === "completed" ? (last.issues > 0 ? "error" : "task_alt") : "pending"}
                </span>
                {t("workday.procedures.last_run").replace(
                  "{date}",
                  new Date(`${last.run_date}T12:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }),
                )}
                {last.issues > 0 && ` · ${t("workday.common.issues").replace("{count}", String(last.issues))}`}
              </div>
            )}
          </div>

          <div class="mt-auto flex items-center justify-between gap-2 border-t border-base-300 pt-4 mt-5">
            <button class="text-sm text-base-content/70 hover:text-base-content" onClick={() => props.onHistory()}>
              {t("workday.procedures.view_history")}
            </button>
            {days ? (
              <button class="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" onClick={() => props.onEdit(true)}>
                {t("workday.procedures.procedure_schedule")}
                <span class="material-symbols-outlined text-base">open_in_new</span>
              </button>
            ) : (
              <button class="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline" onClick={() => props.onEdit(true)}>
                <span class="material-symbols-outlined text-base">add</span>
                {t("workday.procedures.create_schedule")}
              </button>
            )}
          </div>
        </div>
      );
    };
  },
});
